/**
 * Periodenrechnung für den zweiwöchentlichen Business-Manager-Bericht.
 *
 * Perioden werden ausschließlich aus einem festen Ankerdatum abgeleitet - es gibt bewusst keine
 * Perioden-Tabelle und damit nichts zu pflegen. Alle Daten sind reine Kalenderdaten auf
 * UTC-Mitternacht, damit eine Periodengrenze nicht je nach Zeitzone des Servers oder Browsers auf
 * den Vor-/Folgetag rutscht (gleiche Begründung wie formatDateDE() in calculations.ts).
 */
/** Freitag, 11.09.2026 - Tag 1 der Periode 1. */
export const BM_REPORT_ANCHOR_ISO = "2026-09-11";
export const BM_REPORT_PERIOD_LENGTH_DAYS = 14;
/**
 * Tag 12 einer Periode (Dienstag) - Zeitpunkt der ersten Erinnerung. Bewusst nicht der Abgabetag
 * selbst, damit noch Zeit zum Ausfüllen bleibt.
 */
const REMINDER_OFFSET_DAYS = 11;
/**
 * Der Abgabetag ist der Freitag NACH dem letzten Tag der Periode - und damit zugleich der erste
 * Tag der Folgeperiode. An diesem Tag sind zwei Perioden gleichzeitig aktiv (siehe
 * activeBmReportPeriods).
 */
const DUE_OFFSET_DAYS = 14;
/** Auswertungstermin: Dienstag nach dem Abgabetag. */
const MEETING_OFFSET_DAYS = 18;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
function parseIsoDateUtc(iso) {
    const [year, month, day] = iso.split("-").map(Number);
    return new Date(Date.UTC(year, month - 1, day));
}
const ANCHOR = parseIsoDateUtc(BM_REPORT_ANCHOR_ISO);
/** Schneidet einen beliebigen Zeitpunkt auf das zugehörige Kalenderdatum (UTC-Mitternacht) zu. */
export function toUtcDay(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}
function addDays(date, days) {
    return new Date(date.getTime() + days * MS_PER_DAY);
}
export function bmReportPeriodByNumber(periodNumber) {
    if (!Number.isInteger(periodNumber) || periodNumber < 1) {
        throw new Error(`Ungültige Periodennummer: ${periodNumber}`);
    }
    const start = addDays(ANCHOR, (periodNumber - 1) * BM_REPORT_PERIOD_LENGTH_DAYS);
    return {
        number: periodNumber,
        start,
        end: addDays(start, BM_REPORT_PERIOD_LENGTH_DAYS - 1),
        reminderDate: addDays(start, REMINDER_OFFSET_DAYS),
        dueDate: addDays(start, DUE_OFFSET_DAYS),
        meetingDate: addDays(start, MEETING_OFFSET_DAYS),
    };
}
/**
 * Nummer der Periode, in die ein Datum fällt - `null` für Daten vor dem Ankerdatum, für die es
 * keinen Bericht gibt.
 */
export function bmReportPeriodNumberForDate(date) {
    const elapsedDays = Math.floor((toUtcDay(date).getTime() - ANCHOR.getTime()) / MS_PER_DAY);
    if (elapsedDays < 0)
        return null;
    return Math.floor(elapsedDays / BM_REPORT_PERIOD_LENGTH_DAYS) + 1;
}
export function bmReportPeriodForDate(date) {
    const number = bmReportPeriodNumberForDate(date);
    return number === null ? null : bmReportPeriodByNumber(number);
}
/**
 * Alle Perioden, die zum Stichtag bereits begonnen haben - aufsteigend, für die Übersichtsseite
 * "Meine Berichte".
 */
export function bmReportPeriodsUntil(date) {
    const latest = bmReportPeriodNumberForDate(date);
    if (latest === null)
        return [];
    return Array.from({ length: latest }, (_, index) => bmReportPeriodByNumber(index + 1));
}
/**
 * Perioden, die am Stichtag noch bearbeitet werden können: begonnen und Frist nicht überschritten.
 * Am Abgabetag sind das zwei - die abzugebende Periode und die neu gestartete.
 */
export function activeBmReportPeriods(date) {
    const day = toUtcDay(date).getTime();
    const current = bmReportPeriodNumberForDate(date);
    if (current === null)
        return [];
    const candidates = current > 1 ? [current - 1, current] : [current];
    return candidates
        .map(bmReportPeriodByNumber)
        .filter((period) => period.start.getTime() <= day && day <= period.dueDate.getTime());
}
/** Nach Ablauf der Frist ohne Abgabe. */
export function isBmReportOverdue(period, now) {
    return toUtcDay(now).getTime() > period.dueDate.getTime();
}
